import { useEffect, useState } from 'react';
import type { AuthorityRow } from '@shared/types/api';
import { useUpdateAuthority, useUploadServiceImage } from '@/hooks/use-admin';

interface AuthorityImageModalProps {
  authority: AuthorityRow | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AuthorityImageModal({
  authority,
  isOpen,
  onClose,
}: AuthorityImageModalProps) {
  const updateAuthMutation = useUpdateAuthority();
  const uploadImageMutation = useUploadServiceImage();

  const [images, setImages] = useState<string[]>(authority?.images ?? []);
  const [previewUrl, setPreviewUrl] = useState<string | null>(authority?.images?.[0] || null);

  useEffect(() => {
    if (isOpen && authority) {
      setImages(authority.images ?? []);
      setPreviewUrl(authority.images?.[0] || null);
    }
  }, [isOpen, authority]);

  if (!isOpen || !authority) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const key = await uploadImageMutation.mutateAsync(file);
      setImages([key]);
      setPreviewUrl(URL.createObjectURL(file));
    } catch {
      // Error toast managed by TanStack Query mutation
    }
  };

  const handleRemoveImage = () => {
    setImages([]);
    setPreviewUrl(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateAuthMutation.mutateAsync({
        id: authority.id,
        payload: { images },
      });
      onClose();
    } catch {
      // Error toast managed by TanStack Query mutation
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 bg-white">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Authority Image — {authority.name}
            </h3>
            <p className="text-xs text-gray-500">
              Manage image for this authority
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                Authority Image
              </label>

              <div className="flex items-center gap-4">
                {previewUrl ? (
                  <div className="relative h-24 w-32 border border-gray-200 rounded-xl overflow-hidden bg-white p-1 shrink-0 shadow-sm flex items-center justify-center">
                    <img
                      src={previewUrl}
                      alt={authority.name}
                      className="h-full w-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 text-white font-semibold text-xs opacity-0 hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      ✕ Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex h-24 w-32 items-center justify-center border border-dashed border-gray-300 rounded-xl bg-gray-50 text-xs text-gray-400 shrink-0">
                    No Image
                  </div>
                )}

                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    onChange={handleFileChange}
                    className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 file:cursor-pointer hover:file:bg-brand-100"
                  />
                  <p className="text-[11px] text-gray-400">
                    JPG, PNG, WEBP, or SVG. Max 15MB.
                  </p>
                  {uploadImageMutation.isPending && (
                    <p className="text-xs text-brand-600 font-medium">Uploading to S3...</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateAuthMutation.isPending || uploadImageMutation.isPending}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
            >
              {updateAuthMutation.isPending ? 'Saving...' : 'Save Image'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
